import { can, type Permission } from '@bravo/shared';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ClipboardList,
  Landmark,
  LayoutDashboard,
  Lightbulb,
  Scale,
  Settings,
  Shield,
  Users,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiData } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import { useSession } from '../lib/session';
import { usePrefsStore, type ThemeChoice } from '../stores/prefs';
import { Badge } from './ui';

const NAV: { to: string; key: string; icon: typeof LayoutDashboard; allow: Permission[] }[] = [
  { to: '/', key: 'nav.dashboard', icon: LayoutDashboard, allow: ['stats.view'] },
  { to: '/topups', key: 'nav.topups', icon: ArrowDownToLine, allow: ['topups.manage'] },
  {
    to: '/withdrawals',
    key: 'nav.withdrawals',
    icon: ArrowUpFromLine,
    allow: ['withdrawals.manage'],
  },
  { to: '/markets', key: 'nav.markets', icon: Landmark, allow: ['markets.manage'] },
  { to: '/proposals', key: 'nav.proposals', icon: Lightbulb, allow: ['proposals.review'] },
  { to: '/users', key: 'nav.users', icon: Users, allow: ['users.view'] },
  { to: '/admins', key: 'nav.admins', icon: Shield, allow: ['admins.manage'] },
  { to: '/config', key: 'nav.config', icon: Settings, allow: ['config.manage'] },
  {
    to: '/audit',
    key: 'nav.audit',
    icon: ClipboardList,
    allow: ['audit.viewAll', 'audit.viewOwn'],
  },
  { to: '/reconciliation', key: 'nav.reconciliation', icon: Scale, allow: ['reconciliation.view'] },
];

export function Shell() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const theme = usePrefsStore((s) => s.theme);
  const setTheme = usePrefsStore((s) => s.setTheme);
  const setLanguage = usePrefsStore((s) => s.setLanguage);
  const role = session.data?.role;

  async function signOut() {
    await apiData('/admin/auth/logout', { method: 'POST', body: {} });
    queryClient.setQueryData(['me'], null);
  }

  return (
    <div className="min-h-screen md:grid md:grid-cols-[15rem_1fr]">
      <aside className="border-b border-border bg-card md:min-h-screen md:border-b-0 md:border-r">
        <div className="flex items-center gap-3 px-4 py-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-sm font-bold text-brand-fg">
            B
          </span>
          <div>
            <p className="text-sm font-semibold leading-tight">{t('brand')}</p>
            <p className="text-xs text-fg-muted">{t('admin')}</p>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 md:block md:space-y-1 md:overflow-visible">
          {NAV.filter((item) => role && item.allow.some((permission) => can(role, permission))).map(
            (item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  data-testid={`nav-${item.to === '/' ? 'dashboard' : item.to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium ${
                      isActive ? 'bg-brand/10 text-brand-text' : 'text-fg-muted hover:bg-muted'
                    }`
                  }
                >
                  <Icon size={16} />
                  {t(item.key)}
                </NavLink>
              );
            },
          )}
        </nav>
        <div className="space-y-3 border-t border-border px-4 py-4 text-sm">
          <div>
            <p className="font-medium">{session.data?.name}</p>
            <p className="truncate text-xs text-fg-muted">{session.data?.email}</p>
            {role ? (
              <div className="mt-2">
                <Badge>{t(`roles.${role}`)}</Badge>
              </div>
            ) : null}
          </div>
          <label className="block text-xs text-fg-muted">
            {t('language')}
            <select
              className="mt-1 w-full rounded-xl border border-border bg-card px-2 py-1.5 text-sm text-fg"
              value={i18n.language}
              onChange={(event) => {
                const language = event.target.value === 'am' ? 'am' : 'en';
                setLanguage(language);
                void i18n.changeLanguage(language);
              }}
            >
              <option value="en">{t('langEn')}</option>
              <option value="am">{t('langAm')}</option>
            </select>
          </label>
          <label className="block text-xs text-fg-muted">
            {t('theme')}
            <select
              className="mt-1 w-full rounded-xl border border-border bg-card px-2 py-1.5 text-sm text-fg"
              value={theme}
              onChange={(event) => setTheme(event.target.value as ThemeChoice)}
            >
              <option value="system">{t('themeSystem')}</option>
              <option value="light">{t('themeLight')}</option>
              <option value="dark">{t('themeDark')}</option>
            </select>
          </label>
          <button
            type="button"
            className="text-sm font-semibold text-danger"
            onClick={() => void signOut()}
          >
            {t('signOut')}
          </button>
        </div>
      </aside>
      <main className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8">
        <Outlet />
      </main>
    </div>
  );
}
